import React, { useEffect, useRef, useState, useCallback } from 'react';
import { FaArrowDown, FaSpinner } from 'react-icons/fa';
import { useChat } from '../../context/ChatContext';
import useInfiniteScroll from '../../hooks/useInfiniteScroll';
import useTyping from '../../hooks/useTyping';
import MessageItem from './MessageItem';
import '../../css/Chat.css';

const MessageList = ({ chatId, onNewMessage }) => {
  const { 
    messages, 
    loadMessages, 
    loadingMessages, 
    hasMoreMessages 
  } = useChat();
  
  const { typingText, hasTypingUsers } = useTyping(chatId);
  
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [showScrollToBottom, setShowScrollToBottom] = useState(false);
  const [autoScroll, setAutoScroll] = useState(true);
  const previousScrollHeight = useRef(0);

  // Aktuális chat üzenetei
  const chatMessages = messages[chatId] || [];
  const isLoadingMessages = loadingMessages[chatId] || false;
  const hasMore = hasMoreMessages[chatId] !== false;

  // Infinite scroll a régebbi üzenetek betöltéséhez
  const { loading: loadingMore, setObserverTarget } = useInfiniteScroll(
    () => loadMessages(chatId, chatMessages.length),
    hasMore && chatMessages.length > 0
  );

  // Automatikus görgetés az új üzenetekhez
  const scrollToBottom = useCallback((smooth = true) => {
    if (messagesContainerRef.current) {
      const container = messagesContainerRef.current;
      const scrollTo = container.scrollHeight - container.clientHeight;
      
      if (smooth) {
        container.scrollTo({
          top: scrollTo,
          behavior: 'smooth'
        });
      } else {
        container.scrollTop = scrollTo;
      }
    }
  }, []);

  // Scroll pozíció figyelése
  const handleScroll = useCallback(() => {
    if (!messagesContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
    const isNearBottom = scrollHeight - scrollTop - clientHeight < 100;
    
    setShowScrollToBottom(!isNearBottom);
    setAutoScroll(isNearBottom);
  }, []);

  // Chat váltáskor scroll pozíció reset
  useEffect(() => {
    if (chatId) {
      setAutoScroll(true);
      setShowScrollToBottom(false);
      
      // Kis késleltetéssel görgetsünk le
      setTimeout(() => {
        scrollToBottom(false);
      }, 100);
    }
  }, [chatId, scrollToBottom]);

  // Új üzenet esetén automatikus görgetés
  useEffect(() => {
    if (onNewMessage && autoScroll && chatMessages.length > 0) {
      // Kis késleltetéssel görgetsünk le, hogy az új üzenet betöltődjön
      setTimeout(() => {
        scrollToBottom();
      }, 100);
    }
  }, [onNewMessage, autoScroll, chatMessages.length, scrollToBottom]);

  // Régebbi üzenetek betöltése után scroll pozíció megtartása
  useEffect(() => {
    if (messagesContainerRef.current && previousScrollHeight.current > 0) {
      const newScrollHeight = messagesContainerRef.current.scrollHeight;
      const heightDifference = newScrollHeight - previousScrollHeight.current;
      
      if (heightDifference > 0) {
        messagesContainerRef.current.scrollTop += heightDifference;
      }
    }
    previousScrollHeight.current = messagesContainerRef.current?.scrollHeight || 0;
  }, [chatMessages.length]);

  // Load more observer target ref
  const loadMoreRef = useCallback((node) => {
    if (node && hasMore && chatMessages.length > 0) {
      setObserverTarget(node);
    }
  }, [setObserverTarget, hasMore, chatMessages.length]);

  // Üzenetek csoportosítása (ugyanaz a felhasználó, 5 percen belül)
  const groupMessages = (messages) => {
    const grouped = [];
    let currentGroup = null;

    messages.forEach((message, index) => {
      const prevMessage = messages[index - 1];
      const shouldGroup = prevMessage && 
        prevMessage.user_id === message.user_id &&
        new Date(message.created_at) - new Date(prevMessage.created_at) < 2 * 60 * 1000; // 2 perc

      if (shouldGroup && currentGroup) {
        currentGroup.messages.push(message);
      } else {
        // Ellenőrizzük, hogy érvényes-e a created_at
        const validTimestamp = message.created_at && 
          !isNaN(new Date(message.created_at).getTime()) && 
          new Date(message.created_at).getFullYear() !== 1970
          ? message.created_at 
          : new Date().toISOString();
        
        currentGroup = {
          id: message.id || message.temp_id,
          user_id: message.user_id,
          user_name: message.user_name,
          created_at: validTimestamp,
          messages: [message]
        };
        grouped.push(currentGroup);
      }
    });

    return grouped;
  };

  // Dátum elválasztó szükséges-e
  const needsDateSeparator = (currentMessage, prevMessage) => {
    if (!prevMessage) return true;
    
    const currentDate = new Date(currentMessage.created_at).toDateString();
    const prevDate = new Date(prevMessage.created_at).toDateString();
    
    return currentDate !== prevDate;
  };

  // Dátum formázás
  const formatDateSeparator = (timestamp) => {
    if (!timestamp) return 'Ma';
    
    const date = new Date(timestamp);
    
    // Ellenőrizzük, hogy érvényes-e a dátum
    if (isNaN(date.getTime()) || date.getFullYear() === 1970) {
      return 'Ma';
    }
    
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    if (date.toDateString() === today.toDateString()) {
      return 'Ma';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Tegnap';
    } else {
      return date.toLocaleDateString('hu-HU', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  if (!chatId) {
    return (
      <div className="message-list-placeholder">
        <div className="placeholder-content">
          <FaSpinner className="placeholder-icon" />
          <h3>Válassz egy chatet</h3>
          <p>Kezdj beszélgetni a kollégáiddal</p>
        </div>
      </div>
    );
  }

  const groupedMessages = groupMessages(chatMessages);

  return (
    <div className="chat-message-list-container">
      <div 
        className="chat-message-list"
        ref={messagesContainerRef}
        onScroll={handleScroll}
      >
        {/* Betöltési indikátor a tetején */}
        {(loadingMore || isLoadingMessages) && (
          <div className="messages-loading-top">
            <FaSpinner className="loading-spinner" />
            <span>Üzenetek betöltése...</span>
          </div>
        )}

        {/* Load more observer target */}
        {hasMore && chatMessages.length > 0 && (
          <div ref={loadMoreRef} className="load-more-trigger" />
        )}

        {/* Üzenetek megjelenítése */}
        {groupedMessages.length === 0 && !isLoadingMessages ? (
          <div className="chat-message-empty">
            <div className="empty-content">
              <h4>Még nincsenek üzenetek</h4>
              <p>Légy az első, aki üzenetet küld!</p>
            </div>
          </div>
        ) : (
          groupedMessages.map((group, groupIndex) => {
            const prevGroup = groupedMessages[groupIndex - 1];
            const showDateSeparator = !prevGroup || 
              needsDateSeparator(group.messages[0], prevGroup.messages[prevGroup.messages.length - 1]);

            return (
              <React.Fragment key={group.id}>
                {/* Dátum elválasztó */}
                {showDateSeparator && (
                  <div className="date-separator">
                    <span>{formatDateSeparator(group.created_at)}</span>
                  </div>
                )}

                {/* Üzenet csoport */}
                {group.messages.map((message, messageIndex) => (
                  <MessageItem
                    key={message.id || message.temp_id}
                    message={message}
                    showAvatar={messageIndex === 0}
                    isGrouped={messageIndex > 0}
                  />
                ))}
              </React.Fragment>
            );
          })
        )}

        {/* Typing indikátor */}
        {hasTypingUsers && (
          <div className="typing-indicator">
            <div className="typing-animation">
              <span></span>
              <span></span>
              <span></span>
            </div>
            <span className="typing-text">{typingText}</span>
          </div>
        )}

        {/* Scroll anchor */}
        <div ref={messagesEndRef} />
      </div>

      {/* Scroll to bottom gomb */}
      {showScrollToBottom && (
        <button
          className="scroll-to-bottom-btn"
          onClick={() => scrollToBottom()}
          title="Ugrás az utolsó üzenetre"
        >
          <FaArrowDown />
        </button>
      )}
    </div>
  );
};

export default MessageList;
